import { AcceptBid } from '@/components/buttons';
import CancelListing from '@/components/buttons/CancelListing';
import EditListing from '@/components/buttons/EditListing';
import { Transfer } from '@/components/buttons/Transfer/Transfer';
import { UserToken } from '@/hooks/use-user-tokens';
import { useAuthentication } from '@/hooks/useAuthentication';
import { IconButton, Menu, MenuButton, MenuItem, MenuList, useDisclosure } from '@chakra-ui/react';
import { compareAddresses } from '@onbeam/utils';
import { ContextMenuIcon } from '@sphere/icons';
import { DynamicTokens } from '@sphere/reservoir-kit-ui';
import { useInView } from 'framer-motion';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { useEffect, useRef, useState } from 'react';

type Props = {
  token?: DynamicTokens[number] & { ownership?: UserToken['ownership'] };
  onModalOpen?: () => void;
  onModalClose?: () => void;
  onRefresh?: () => void;
};

export const NftCardContextMenu = ({ token, onModalOpen, onModalClose, onRefresh }: Props) => {
  const { userAddress } = useAuthentication();
  const menuDisclosure = useDisclosure();
  const { t } = useTranslation('nft');
  const ref = useRef(null);
  const isInView = useInView(ref);
  const acceptOpenState = useState(false);
  const cancelOpenState = useState(false);
  const editOpenState = useState(false);
  const transferOpenState = useState(false);

  const openStates = [
    acceptOpenState[0],
    cancelOpenState[0],
    editOpenState[0],
    transferOpenState[0],
  ];

  useEffect(() => {
    if (openStates.some(isOpen => isOpen)) {
      onModalOpen?.();
    } else {
      onModalClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, openStates);

  useEffect(() => {
    if (!isInView) {
      menuDisclosure.onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  const tokenId = token?.token?.tokenId;
  const collectionId = token?.token?.collection?.id;
  const listingId =
    compareAddresses(token?.market?.floorAsk?.maker, userAddress) && token?.market?.floorAsk?.id;
  const bidId =
    !compareAddresses(token?.market?.topBid?.maker, userAddress) && token?.market?.topBid?.id;
  const isOracleOrder = token?.ownership?.floorAsk?.isNativeOffChainCancellable;

  if (!tokenId || (!bidId && !listingId && !token.ownership)) return null;

  return (
    <Menu placement="bottom-end" {...menuDisclosure}>
      <MenuButton
        ref={ref}
        data-testid="nft-context-menu-button"
        as={IconButton}
        boxSize={rem(40)}
        variant="secondary"
        aria-label={t('components.context-menu.title')}
        icon={<ContextMenuIcon />}
        alignItems="center"
      />
      <MenuList
        data-testid="nft-context-menu-list"
        zIndex="in-front"
        px={{ base: 0, md: 'space.8' }}
      >
        {token.ownership && token?.token && (
          <Transfer
            tokenName={`${token?.token.name ?? `#${token?.token.tokenId}`}`}
            contractAddress={token?.token.contract}
            tokenId={tokenId}
            is1155={token?.token.kind === 'erc1155'}
            ownedQuantity={Number(token.ownership?.tokenCount || 1)}
            trigger={
              <MenuItem fontSize={{ base: 'sm', md: 'md' }}>
                {t('components.context-menu.transfer')}
              </MenuItem>
            }
            openState={transferOpenState}
            onClose={isTransferred => isTransferred && onRefresh?.()}
          />
        )}
        {bidId && (
          <AcceptBid
            trigger={
              <MenuItem fontSize={{ base: 'sm', md: 'md' }}>
                {t('components.context-menu.accept-highest-offer')}
              </MenuItem>
            }
            tokenId={tokenId}
            bidId={bidId}
            collectionId={collectionId}
            openState={acceptOpenState}
          />
        )}
        {listingId && (
          <CancelListing
            listingId={listingId}
            trigger={
              <MenuItem fontSize={{ base: 'sm', md: 'md' }}>
                {t('components.context-menu.cancel-listing')}
              </MenuItem>
            }
            openState={cancelOpenState}
          />
        )}
        {isOracleOrder && tokenId && collectionId && listingId && (
          <EditListing
            listingId={listingId}
            tokenId={tokenId}
            collectionId={collectionId}
            trigger={
              <MenuItem fontSize={{ base: 'sm', md: 'md' }}>
                {t('components.context-menu.edit-listing')}
              </MenuItem>
            }
            openState={editOpenState}
          />
        )}
      </MenuList>
    </Menu>
  );
};
